import {fromSeconds} from 'common/date';
import SharePrice from 'common/SharePrice';
import * as React from 'react';
import {
  buildSaleTotals,
  formatCents,
  issuerCurrency,
  type EquityEventData,
  type EquityIssuerData,
} from 'src/EquityView/equitySeries';
import {cn} from 'src/lib/utils';

const dateLabel = (seconds: number): string =>
  fromSeconds(seconds).toLocaleString({
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

/**
 * The money waterfall of one sale: gross split into what taxes and exercise
 * cost took versus what was actually banked. Widths are proportional so the
 * 2023 tender's "why was the net only 2M of 4.66M" is visible at a glance.
 */
const Waterfall = ({
  event,
  currency,
}: {
  event: EquityEventData;
  currency: string;
}) => {
  const cents = (value: number): string => formatCents(value, currency);
  // Only an UNKNOWN gross has nothing to draw — a zero gross with positive
  // costs is a valid all-loss event whose shortfall must still show.
  const gross = event.grossCents;
  if (gross == null) return null;
  const withholding = event.withholdingCents;
  const exercise = event.exerciseCostCents;
  const net = event.netCents ?? gross - withholding - exercise;
  const segments = [
    {label: 'Net', value: net, className: 'bg-chart-3'},
    {label: 'Withholding', value: withholding, className: 'bg-chart-8'},
    {label: 'Exercise cost', value: exercise, className: 'bg-chart-4'},
  ].filter((segment) => segment.value > 0);
  // Costs can legitimately exceed gross (net negative); scale to whichever
  // is larger so the bar never clips, and call the shortfall out in text
  // instead of hiding the loss.
  const scale = Math.max(gross, withholding + exercise);
  if (segments.length === 0 || scale <= 0) return null;
  return (
    <div className="flex flex-col gap-1">
      <div className="border-border flex h-2.5 w-full overflow-hidden rounded-full border">
        {segments.map((segment) => (
          <div
            key={segment.label}
            className={segment.className}
            style={{width: `${(segment.value / scale) * 100}%`}}
          />
        ))}
      </div>
      <div className="text-muted-foreground flex flex-wrap gap-x-3 gap-y-0.5 text-xs">
        {segments.map((segment) => (
          <span key={segment.label} className="flex items-center gap-1">
            <span
              className={cn('inline-block size-2 rounded-full', segment.className)}
            />
            {segment.label} {cents(segment.value)}
            {gross > 0 && ` (${Math.round((segment.value / gross) * 100)}%)`}
          </span>
        ))}
        {net < 0 && (
          <span className="text-destructive font-medium">
            Shortfall {cents(net)} — costs exceeded gross
          </span>
        )}
      </div>
    </div>
  );
};

/** Liquidity events, newest first — one card per sale with its waterfall. */
export const EventsList = ({
  issuer,
  todaySeconds,
}: {
  issuer: EquityIssuerData;
  todaySeconds: number;
}) => {
  const currency = issuerCurrency(issuer);
  const cents = (value: number): string => formatCents(value, currency);
  const totals = buildSaleTotals(issuer, todaySeconds);
  // Same cutoff as the header totals — a future-dated event can't show a
  // card above a summary that excludes it.
  const events = React.useMemo(
    () =>
      issuer.events
        .filter((event) => event.date <= todaySeconds)
        .sort((a, b) => b.date - a.date),
    [issuer, todaySeconds],
  );
  if (events.length === 0) {
    return (
      <div className="text-muted-foreground py-6 text-center text-sm">
        {issuer.events.length > 0
          ? `No liquidity events yet — ${issuer.events.length} future-dated event${
              issuer.events.length === 1 ? '' : 's'
            } not shown.`
          : 'No liquidity events recorded yet.'}
      </div>
    );
  }
  return (
    <section>
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h2 className="text-foreground text-base font-semibold">
          Liquidity events
        </h2>
        <span className="text-muted-foreground text-xs tabular-nums">
          {totals.sharesSold.toLocaleString('en-US')} sh sold
          {totals.grossCents != null && ` · gross ${cents(totals.grossCents)}`}
          {totals.netCents != null &&
            ` · net ${cents(totals.netCents)}${
              totals.netUnknownCount > 0
                ? ` (excl. ${totals.netUnknownCount} unknown)`
                : ''
            }`}
          {totals.undocumentedCount > 0 &&
            ` · ${totals.undocumentedCount} undocumented excluded`}
          {totals.excludedCurrencyCount > 0 &&
            ` · ${totals.excludedCurrencyCount} other-currency excluded`}
          {issuer.events.length > events.length &&
            ` · ${issuer.events.length - events.length} future-dated not shown`}
        </span>
      </div>
      <div className="flex flex-col gap-2">
        {events.map((event) => {
          const price =
            event.price != null
              ? SharePrice.parse(event.price, event.currency)
              : null;
          return (
            <div
              key={event._id}
              className="border-border rounded-md border p-3"
            >
              <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                <div className="flex items-baseline gap-2">
                  <span className="text-foreground font-medium">
                    {event.name}
                  </span>
                  <span className="text-muted-foreground text-xs">
                    {dateLabel(event.date)}
                  </span>
                  {event.estimated && (
                    <span className="border-border text-muted-foreground rounded-full border px-1.5 py-0.5 text-[10px] font-medium">
                      estimated
                    </span>
                  )}
                  {event.missingTransactionLinks > 0 && (
                    <span className="border-destructive/40 text-destructive rounded-full border px-1.5 py-0.5 text-[10px] font-medium">
                      {event.missingTransactionLinks} broken link
                      {event.missingTransactionLinks === 1 ? '' : 's'}
                    </span>
                  )}
                </div>
                <span className="text-muted-foreground text-sm tabular-nums">
                  {event.sharesSold.toLocaleString('en-US')} sh
                  {price ? ` @ ${price.format()}` : ''}
                  {event.grossCents != null
                    ? ` → ${cents(event.grossCents)} gross`
                    : ''}
                </span>
              </div>
              <div className="mt-2">
                <Waterfall event={event} currency={currency} />
              </div>
              <div className="text-muted-foreground mt-1.5 flex flex-wrap gap-x-4 gap-y-0.5 text-xs">
                {event.netCents != null && (
                  <span>
                    Net{' '}
                    <span className="text-foreground font-medium tabular-nums">
                      {cents(event.netCents)}
                    </span>
                  </span>
                )}
                {event.realizedGainCents != null && (
                  <span>
                    Realized gain{' '}
                    <span className="text-foreground font-medium tabular-nums">
                      {cents(event.realizedGainCents)}
                    </span>
                  </span>
                )}
                <span>
                  Drawn from {event.draws.length} lot
                  {event.draws.length === 1 ? '' : 's'}
                </span>
                {event.note && <span>{event.note}</span>}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};
